<!-- src/components/rota/StaffDetailsManager.vue -->
<template>
  <div class="sd-wrap">
    <div class="core-actions core-actions--right sd-header">
      <div class="core-actions sd-actions">
        <a
          class="btn"
          :class="{
            disabled: busyAll || !canSave,
          }"
          @click.prevent="onSave"
        >
          {{ editingId ? "Save Staff" : "Add Staff" }}
        </a>

        <a
          class="btn-flat"
          :class="{
            disabled: busyAll || !editingId,
          }"
          @click.prevent="onClear"
        >
          Clear
        </a>
      </div>
    </div>

    <div
      v-if="error"
      class="card-panel red lighten-5 red-text text-darken-3"
      style="margin-top: 12px"
    >
      {{ error }}
    </div>

    <div class="row" style="margin-top: 12px; margin-bottom: 0">
      <div class="input-field col s12 m4">
        <input id="sd_name" v-model="form.staff_name" type="text" :disabled="busyAll" />

        <label class="active" for="sd_name"> Staff Name </label>
      </div>

      <div class="input-field col s12 m4">
        <input
          id="sd_email"
          v-model="form.email"
          type="email"
          :disabled="busyAll"
          placeholder="name@company.com"
          autocomplete="off"
        />

        <label class="active" for="sd_email"> Email </label>
      </div>

      <div class="input-field col s12 m4">
        <select ref="jobTitleSelect" v-model="form.job_title" :disabled="busyAll">
          <option value="" disabled>Select job title</option>

          <option v-for="j in jobTitles" :key="j" :value="j">
            {{ j }}
          </option>
        </select>

        <label> Job Title </label>
      </div>
    </div>

    <div class="row" style="margin-top: 0; margin-bottom: 0">
      <div class="input-field col s12 m3">
        <input
          id="sd_rate"
          v-model.number="form.pay_rate"
          type="number"
          step="0.01"
          :disabled="busyAll"
        />

        <label class="active" for="sd_rate"> Pay Rate (£/hr) </label>
      </div>

      <div class="input-field col s12 m3" style="margin-top: 22px">
        <label>
          <input v-model="form.active" type="checkbox" :disabled="busyAll" />

          <span> Active </span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { getFunctions, httpsCallable } from "firebase/functions";

import { app } from "@/firebase";

import { upsertStaffDetail } from "@/scripts/sqlApiHelper/staffDetailsApi";

import { inviteStaffRotaViewer } from "@/scripts/rotaInvite";

/* global M */

function normEmail(v) {
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

function isValidEmail(v) {
  const s = normEmail(v);

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function isValidEmailOrBlank(v) {
  const s = normEmail(v);

  if (!s) {
    return true;
  }

  return isValidEmail(s);
}

async function ensureStaffLoginCode({ venueId, staffId }) {
  const functions = getFunctions(app, "us-central1");

  const callable = httpsCallable(functions, "ensureStaffLoginCode");

  const res = await callable({
    venueId,
    staffId,
  });

  return res.data || {};
}

export default {
  name: "StaffDetailsManager",

  props: {
    venueId: {
      type: String,
      required: true,
    },

    busy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      error: null,

      editingId: null,

      busyLocal: false,

      jobTitles: [
        "General Manager",
        "Assistant Manager",
        "Supervisor",
        "Bartender",
        "Floorstaff",
        "Cleaner",
        "Kitchen Manager",
        "Chef",
        "Kitchen Porter",
      ],

      form: {
        staff_name: "",

        email: "",

        job_title: "",

        pay_rate: null,

        active: true,
      },
    };
  },

  computed: {
    busyAll() {
      return !!this.busy || this.busyLocal;
    },

    canSave() {
      return (
        !!this.venueId &&
        !!String(this.form.staff_name || "").trim() &&
        !!String(this.form.job_title || "").trim() &&
        Number.isFinite(Number(this.form.pay_rate)) &&
        Number(this.form.pay_rate) >= 0 &&
        isValidEmailOrBlank(this.form.email)
      );
    },
  },

  mounted() {
    this.initMaterialize();
  },

  updated() {
    this.initMaterialize();
  },

  beforeUnmount() {
    this.destroyMaterialize();
  },

  methods: {
    initMaterialize() {
      this.$nextTick(() => {
        if (!window.M?.FormSelect) {
          return;
        }

        const el = this.$refs.jobTitleSelect;

        if (!el) {
          return;
        }

        const inst = window.M.FormSelect.getInstance(el);

        if (inst) {
          inst.destroy();
        }

        window.M.FormSelect.init(el);
      });
    },

    destroyMaterialize() {
      try {
        const el = this.$refs.jobTitleSelect;

        if (!el || !window.M?.FormSelect) {
          return;
        }

        window.M.FormSelect.getInstance(el)?.destroy();
      } catch {
        // Ignore Materialize cleanup errors.
      }
    },

    toast(msg) {
      window.M?.toast?.({
        html: msg,
      });
    },

    onEdit(s) {
      this.error = null;

      this.editingId = s?.id || null;

      this.form = {
        staff_name: s?.staff_name || "",

        email: s?.email || "",

        job_title: s?.job_title || "",

        pay_rate: s?.pay_rate ?? null,

        active: s?.active !== false,
      };

      this.toast("Loaded staff into editor");
    },

    onClear() {
      this.error = null;

      this.editingId = null;

      this.form = {
        staff_name: "",

        email: "",

        job_title: "",

        pay_rate: null,

        active: true,
      };
    },

    async onSave() {
      try {
        this.error = null;

        this.busyLocal = true;

        const wasNew = !this.editingId;

        const email = normEmail(this.form.email);

        if (!isValidEmailOrBlank(email)) {
          this.error = "Please enter a valid email address.";

          return;
        }

        const payload = {
          id: this.editingId,

          staff_name: String(this.form.staff_name || "").trim(),

          email,

          job_title: String(this.form.job_title || "").trim(),

          pay_rate: Number(this.form.pay_rate),

          active: this.form.active !== false,
        };

        /*
         * STEP 1
         * Save the actual staff_details record.
         */
        const res = await upsertStaffDetail(this.venueId, payload);

        const savedId = String(res?.id || "").trim();

        if (!savedId) {
          throw new Error("Staff record was saved without a staff ID.");
        }

        this.editingId = savedId;

        /*
         * STEP 2
         * Ensure the staff member has a six-digit Staff App code.
         */
        let staffLoginCode = "";

        try {
          const codeRes = await ensureStaffLoginCode({
            venueId: this.venueId,

            staffId: savedId,
          });

          if (codeRes?.ok === false) {
            throw new Error(codeRes?.error || "Staff login code could not be created.");
          }

          staffLoginCode = String(codeRes?.pin || codeRes?.codeId || "").trim();

          if (!staffLoginCode) {
            throw new Error("Staff login code was not returned.");
          }

          this.toast(
            codeRes?.created
              ? `Staff login code created: ${staffLoginCode}`
              : `Staff login code already exists: ${staffLoginCode}`
          );
        } catch (codeErr) {
          console.error("Staff login code creation failed:", codeErr);

          this.error =
            codeErr?.message ||
            "Staff saved, but the staff login code could not be created.";

          this.toast("Staff saved, but login code failed");

          return;
        }

        /*
         * STEP 3
         * Only send the welcome/code email when creating a new staff member.
         */
        if (wasNew && email && staffLoginCode) {
          try {
            await inviteStaffRotaViewer({
              email,

              venueId: this.venueId,

              staffId: savedId,

              staffName: payload.staff_name,

              jobTitle: payload.job_title,

              staffLoginCode,
            });

            this.toast("Staff app code emailed to staff");
          } catch (inviteErr) {
            console.error("Staff app email failed:", inviteErr);

            this.error =
              inviteErr?.message ||
              "Staff saved, but the staff app email could not be sent.";

            this.toast("Staff saved, but staff app email failed");

            return;
          }
        }

        this.toast("Staff saved");

        this.$emit("saved");
      } catch (e) {
        console.error("Staff save failed:", e);

        this.error = e?.message || String(e);
      } finally {
        this.busyLocal = false;
      }
    },
  },
};
</script>

<style scoped>
.sd-wrap {
  width: 100%;
}

.sd-actions .btn,
.sd-actions .btn-flat {
  white-space: nowrap;
}

.sd-wrap .row {
  margin-bottom: 0;
}

@media (max-width: 600px) {
  .sd-actions .btn-flat {
    width: 100% !important;
    text-align: center;
  }

  .sd-wrap .input-field[style*="margin-top"] {
    margin-top: 8px !important;
  }

  .sd-wrap label span {
    line-height: 1.35;
  }
}
</style>
